import { useTranslation } from "react-i18next";
import {
  CreateHomeworkAssignmentRequestBody,
  QuestionPropsQuestionType,
} from "@packages/api/madlenAPI.schemas";
import { Button, Text } from "../../../../../core";
import { Input } from "@core/input";
import * as HoverCard from "@radix-ui/react-hover-card";
import { IoInformationCircleOutline } from "react-icons/io5";
import multipleIcon from "@packages/assets/multiple.png";
import optionIcon from "@packages/assets/option.png";
import voteIcon from "@packages/assets/vote.png";
import openEndedIcon from "@packages/assets/open_ended_icon.png";

const questionTypeIcons: Record<string, string> = {
  TEST: multipleIcon,
  TF: voteIcon,
  FB: optionIcon,
  OE: openEndedIcon,
};

const QuestionDistributionForm = ({
  assignmentData,
  onChange,
}: {
  assignmentData: CreateHomeworkAssignmentRequestBody;
  onChange: (data: Partial<CreateHomeworkAssignmentRequestBody>) => void;
}) => {
  const { t } = useTranslation();

  const handleQuestionTypeChange = (
    type: QuestionPropsQuestionType,
    field: "count" | "point",
    value: number,
  ) => {
    // Allow 0 for both count and point
    const finalValue = Math.max(0, value);

    onChange({
      questionTypes: [
        ...assignmentData.questionTypes.filter((q) => q.questionType !== type),
        {
          questionType: type,
          count:
            field === "count"
              ? finalValue
              : Math.max(
                  0,
                  assignmentData.questionTypes.find(
                    (q) => q.questionType === type,
                  )?.count ?? 0,
                ),
          point:
            field === "point"
              ? finalValue
              : (assignmentData.questionTypes.find(
                  (q) => q.questionType === type,
                )?.point ?? 0),
          difficulty: assignmentData.assignmentDifficulty,
        },
      ],
    });
  };

  const handleDifficultyChange = (
    difficulty: CreateHomeworkAssignmentRequestBody["assignmentDifficulty"],
  ) => {
    const updatedData: Partial<CreateHomeworkAssignmentRequestBody> = {
      assignmentDifficulty: difficulty,
      questionTypes: assignmentData.questionTypes.map((qType) => ({
        ...qType,
        difficulty: difficulty,
        // Keep current count, don't force minimum 1
        count: qType.count ?? 0,
      })),
    };

    onChange(updatedData);
  };

  return (
    <div className="space-y-8 mt-8">
      <div className="grid grid-cols-1 gap-8">
        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700">
          <div className="flex gap-3">
            {["EASY", "MEDIUM", "HARD"].map((difficulty) => {
              const getSelectedColors = () => {
                switch (difficulty) {
                  case "EASY":
                    return "bg-green-600 dark:bg-green-500 text-white";
                  case "MEDIUM":
                    return "bg-amber-600 dark:bg-amber-500 text-white";
                  case "HARD":
                    return "bg-purple-600 dark:bg-purple-500 text-white";
                  default:
                    return "bg-blue-600 dark:bg-blue-500 text-white";
                }
              };

              return (
                <Button
                  key={difficulty}
                  onClick={() =>
                    handleDifficultyChange(
                      difficulty as CreateHomeworkAssignmentRequestBody["assignmentDifficulty"],
                    )
                  }
                  bgColor={`${
                    assignmentData.assignmentDifficulty === difficulty
                      ? ""
                      : "bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600"
                  }`}
                  textColor={`${
                    assignmentData.assignmentDifficulty === difficulty
                      ? ""
                      : "text-gray-700 dark:text-gray-200"
                  }`}
                  rounded="lg"
                  className={`flex-1 py-3 px-4 ${
                    assignmentData.assignmentDifficulty === difficulty
                      ? getSelectedColors() + " shadow-md"
                      : ""
                  }`}
                >
                  {t(
                    `assignments.generator.questions.difficulties.${difficulty.toLowerCase()}`,
                  )}
                </Button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <Text variant="h3" className="mb-6">
          assignments.generator.questions.distribution
        </Text>
        <div className="flex flex-col md:grid md:grid-cols-2 gap-6">
          {Object.values(QuestionPropsQuestionType)
            .filter((type) => type !== "MATCH")
            .map((type) => {
              const qType = assignmentData.questionTypes.find(
                (q) => q.questionType === type,
              ) || {
                questionType: type,
                count: 0, // Default to 0 instead of 1
                point: 0,
                difficulty: assignmentData.assignmentDifficulty,
              };

              // Allow 0 count
              const currentCount = Math.max(0, qType.count ?? 0);

              return (
                <div
                  key={type}
                  className="bg-gray-50 dark:bg-gray-700 rounded-xl p-5 h-full"
                >
                  <div className="flex justify-center items-center gap-4 mb-2">
                    <img
                      src={questionTypeIcons[type]}
                      alt={t(`assignments.generator.questions.types.${type}`)}
                      className="w-10 h-10 object-contain"
                      loading="lazy"
                    />
                    <label className="block text-base font-medium text-gray-800 dark:text-gray-200">
                      {t(`assignments.generator.questions.types.${type}`)}
                    </label>
                  </div>
                  <div className="flex flex-col md:flex-row md:items-center gap-6">
                    <div>
                      <label className="block text-sm text-center md:text-left text-gray-600 dark:text-gray-300 mb-2">
                        {t("assignments.generator.questions.count")}
                      </label>
                      <div className="flex items-center justify-center md:justify-start gap-3">
                        <Button
                          onClick={() =>
                            handleQuestionTypeChange(
                              type,
                              "count",
                              Math.max(0, currentCount - 1), // Allow decrease to 0
                            )
                          }
                          textColor="text-gray-600 dark:text-gray-300"
                          bgColor="bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500"
                          rounded="lg"
                          size="sm"
                          disabled={currentCount <= 0} // Disable when at 0
                        >
                          -
                        </Button>
                        <Input
                          type="number"
                          value={currentCount}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^\d]/g, "");
                            const intValue =
                              value === "" ? 0 : Math.max(0, parseInt(value)); // Allow 0
                            handleQuestionTypeChange(type, "count", intValue);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "." || e.key === ",") {
                              e.preventDefault();
                            }
                          }}
                          min={0} // Set minimum to 0
                          step="1"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          size="32px"
                          classNames={{
                            input:
                              "w-16 p-2 border rounded-lg text-center bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                          }}
                        />
                        <Button
                          onClick={() =>
                            handleQuestionTypeChange(
                              type,
                              "count",
                              currentCount + 1,
                            )
                          }
                          textColor="dark:text-gray-300"
                          bgColor="bg-gray-100 dark:bg-gray-600 hover:bg-gray-200 dark:hover:bg-gray-500 text-gray-600"
                          rounded="lg"
                          size="sm"
                        >
                          +
                        </Button>
                      </div>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <label className="block text-sm text-gray-600 dark:text-gray-300">
                          {t("assignments.generator.questions.points")}
                        </label>
                        <HoverCard.Root openDelay={0} closeDelay={0}>
                          <HoverCard.Trigger asChild>
                            <button className="focus:outline-none">
                              <IoInformationCircleOutline className="w-4 h-4 text-gray-600 hover:text-gray-700 dark:text-gray-300 dark:hover:text-gray-400" />
                            </button>
                          </HoverCard.Trigger>
                          <HoverCard.Portal>
                            <HoverCard.Content
                              className="bg-white dark:bg-gray-800 rounded-lg shadow-lg px-3 py-2 text-sm"
                              side="top"
                              align="center"
                              sideOffset={5}
                            >
                              {t(
                                "assignments.generator.questions.pointIntroduction",
                              )}
                              <HoverCard.Arrow className="fill-white dark:fill-gray-800" />
                            </HoverCard.Content>
                          </HoverCard.Portal>
                        </HoverCard.Root>
                      </div>
                      <Input
                        type="range"
                        min={0} // Points can still be 0
                        key={type}
                        max={10}
                        value={qType.point ?? 0}
                        onChange={(e) =>
                          handleQuestionTypeChange(
                            type,
                            "point",
                            Number(e.target.value),
                          )
                        }
                        classNames={{
                          input:
                            "w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600 dark:accent-blue-500",
                        }}
                      />
                      <div className="mt-2 text-center font-medium text-gray-700 dark:text-gray-200">
                        {t("assignments.generator.questions.points")}:{" "}
                        {qType.point ?? 0}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default QuestionDistributionForm;
